/**
 * ====================================================================
 * Axios Interceptor للتعامل مع انتهاء الاشتراك
 * ====================================================================
 */

import axios from "axios";
import { API_URL } from "../config/api.config";
import logger from "./logger";

// إعداد base URL
axios.defaults.baseURL = API_URL;

/**
 * دالة للحصول على subdomain
 * يحاول استخراجه من:
 * 1. localStorage (tenant info من Login)
 * 2. window.location.hostname (إذا كان subdomain موجود)
 * 3. افتراضي: 'default'
 */
const getTenantSubdomain = () => {
  // 1. محاولة الحصول من localStorage
  const tenantInfo = localStorage.getItem("tenantInfo");
  if (tenantInfo) {
    try {
      const parsed = JSON.parse(tenantInfo);
      if (parsed.subdomain) {
        return parsed.subdomain;
      }
    } catch (e) {
      logger.error("Error parsing tenantInfo:", e);
    }
  }

  // 2. محاولة الحصول من hostname
  const hostname = window.location.hostname;
  const parts = hostname.split(".");

  // إذا كان subdomain موجود (مثلاً: tenant-aaaa.localhost.com)
  if (parts.length > 2 || (parts.length === 2 && parts[0] !== "localhost")) {
    return parts[0];
  }

  // 3. افتراضي (للتطوير)
  return "tenant_aaaa"; // يمكن تغييره حسب الحاجة
};

// إضافة tenant subdomain للـ headers
let tenantSubdomain = getTenantSubdomain();
axios.defaults.headers.common["X-Tenant-Subdomain"] = tenantSubdomain;

// تحديث subdomain عند تغييره
export const updateTenantSubdomain = (subdomain) => {
  tenantSubdomain = subdomain;
  axios.defaults.headers.common["X-Tenant-Subdomain"] = subdomain;
  logger.debug("✅ Tenant subdomain updated to:", subdomain);
};

// Interceptor للـ responses
axios.interceptors.response.use(
  (response) => {
    // التحقق من وجود تحذير اشتراك في الـ headers
    const warning = response.headers["x-subscription-warning"];
    const daysRemaining = response.headers["x-days-remaining"];

    if (warning && daysRemaining) {
      // عرض تحذير للمستخدم
      showSubscriptionWarning(warning, daysRemaining);
    }

    return response;
  },
  (error) => {
    // التحقق من كود الخطأ 402: Payment Required (اشتراك منتهي)
    if (error.response && error.response.status === 402) {
      const data = error.response.data;

      if (data.subscriptionExpired) {
        // حفظ معلومات الاشتراك في localStorage
        localStorage.setItem("subscriptionExpired", "true");
        localStorage.setItem(
          "subscription",
          JSON.stringify(data.subscriptionStatus)
        );
        localStorage.setItem("tenantInfo", JSON.stringify(data.tenant));

        // إعادة توجيه لصفحة انتهاء الاشتراك
        window.location.href = "/subscription-expired";

        return Promise.reject(error);
      }
    }

    // التحقق من خطأ 401: Unauthorized
    if (error.response && error.response.status === 401) {
      // إعادة توجيه لصفحة تسجيل الدخول
      localStorage.removeItem("token");
      window.location.href = "/login";
    }

    return Promise.reject(error);
  }
);

// Request interceptor لإضافة token
axios.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("token");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

/**
 * عرض تحذير الاشتراك
 */
let warningShown = false;

const showSubscriptionWarning = (message, daysRemaining) => {
  // عرض التحذير مرة واحدة فقط في الجلسة
  if (warningShown) return;

  warningShown = true;

  // يمكن استخدام مكتبة notification مثل antd
  if (window.antd && window.antd.notification) {
    window.antd.notification.warning({
      message: "تحذير الاشتراك",
      description: message,
      duration: 10,
      placement: "topLeft",
      style: { direction: "rtl" },
      btn:
        daysRemaining <= 3 ? (
          <button
            onClick={() => (window.location.href = "/subscription/plans")}
            style={{
              background: "#1890ff",
              color: "white",
              border: "none",
              padding: "8px 16px",
              borderRadius: "4px",
              cursor: "pointer",
            }}
          >
            جدد الآن
          </button>
        ) : null,
    });
  } else {
    // fallback إلى alert عادي
    logger.warn("Subscription Warning:", message);
  }
};

/**
 * دالة للتحقق من حالة الاشتراك
 */
export const checkSubscriptionStatus = async () => {
  try {
    const response = await axios.get("/api/subscription/status");

    if (response.data.success) {
      const { subscription } = response.data.data;

      if (!subscription.isActive) {
        // الاشتراك غير نشط
        localStorage.setItem("subscriptionExpired", "true");
        localStorage.setItem(
          "subscription",
          JSON.stringify(response.data.data.subscription)
        );
        return false;
      } else {
        // الاشتراك نشط
        localStorage.removeItem("subscriptionExpired");

        // عرض تحذير إذا كان قريب من الانتهاء
        if (subscription.showWarning) {
          showSubscriptionWarning(
            subscription.warningMessage,
            subscription.daysRemaining
          );
        }

        return true;
      }
    }

    return true; // افتراضياً نسمح بالمرور
  } catch (error) {
    logger.error("Error checking subscription:", error);
    return true; // في حالة الخطأ، نسمح بالمرور
  }
};

/**
 * دالة للتحقق قبل التوجيه
 */
export const subscriptionGuard = async (to, from, next) => {
  // صفحات لا تحتاج تحقق
  const publicPages = [
    "/login",
    "/register",
    "/subscription-expired",
    "/subscription/plans",
    "/payment",
  ];

  if (publicPages.includes(to.path)) {
    next();
    return;
  }

  // التحقق من localStorage أولاً (أسرع)
  const subscriptionExpired = localStorage.getItem("subscriptionExpired");

  if (subscriptionExpired === "true") {
    next("/subscription-expired");
    return;
  }

  // التحقق من السيرفر
  const isActive = await checkSubscriptionStatus();

  if (!isActive) {
    next("/subscription-expired");
  } else {
    next();
  }
};

export default axios;
